import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { capitalizeFirstLetter } from '../../../../../utils/commonFunctions';
import URLs from '../../../../../utils/appURLs';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';
import axiosInstance from '../../../../../app/config/axiosInstance';
type ReturnedType = any;
type ThunkArg = {
  breadth: number;
  description: string;
  dimensionUnit: string;
  freightNumber: string;
  freightUnladenWeight: number;
  freightUnladenWeightUnit: string;
  length: number;
  ownerName: string;
  ownership: string;
  width: number;
};

interface freight {
  breadth: number;
  data?: any;
  description: string;
  dimensionUnit: string;
  freightNumber: string;
  freightUnladenWeight: number;
  freightUnladenWeightUnit: string;
  length: number;
  ownerName: string;
  ownership: string;
  width: number;
}

type initialStateProps = {
  isLoading: boolean;
  data: any;
  error: string | null;
  count: null;
};

const initialState: initialStateProps = {
  isLoading: false,
  data: [],
  error: null,
  count: null
};

const pagination = {
  pageNo: 1,
  pageSize: 5
};

// get freight
export const getFreight = createAsyncThunk<ReturnedType, any>(
  'user/freight/get',
  async (data, thunkApi) => {
    try {
      const {
        pageNo = 1,
        pageSize = 5,
        sortColumnName,
        sortBy,
        searchInput,
        searchColumnName,
        signal
      } = data;

      let url = `${URLs.GET_FREIGHT}?pageNo=${pageNo}&pageSize=${pageSize}`;

      if (sortBy && sortColumnName && searchColumnName && searchInput) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}&searchInput=${searchInput}&searchColumnName=${searchColumnName}`;
      } else if (sortBy && sortColumnName && searchInput) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}&searchInput=${searchInput}`;
      } else if (searchInput && searchColumnName) {
        url += `&searchInput=${searchInput}&searchColumnName=${searchColumnName}`;
      } else if (sortBy && sortColumnName && searchInput && searchColumnName) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}&searchInput=${searchInput}&searchColumnName=${searchColumnName}`;
      } else if (sortBy && sortColumnName && searchInput) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}&searchInput=${searchInput}`;
      } else if (searchInput) {
        url += `&searchInput=${searchInput}`;
      } else if (sortBy && sortColumnName) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}`;
      }
      const response = await axiosInstance({
        method: 'GET',
        url: url,
        signal: signal
      });
      return response?.data;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: error?.response?.data?.message
              ? error?.response?.data?.message
              : error?.message,
            severity: 'error'
          })
        );
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

// add freight
export const addFreight = createAsyncThunk<ReturnedType, ThunkArg>(
  'user/freight/add',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'POST',
        url: `${URLs.ADD_FREIGHT}`,
        data
      });
      await thunkApi.dispatch(getFreight(pagination));
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response.data.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response.data?.error,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// update freight
export const updateFreight = createAsyncThunk<ReturnedType, any>(
  'update/Freight',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'PUT',
        url: `${URLs.UPDATE_FREIGHT}${data.id}`,
        data: data.updatePayload
      });
      await thunkApi.dispatch(getFreight(pagination));
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.error,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// delete freight
export const deleteFreight = createAsyncThunk<ReturnedType, string>(
  'Freight/delete',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'DELETE',
        url: `${URLs.DELETE_FREIGHT}${data}`
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.error,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// patch freight
export const PatchFreight = createAsyncThunk<ReturnedType, string>(
  'Freight/patch',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'PATCH',
        url: `${URLs.PATCH_FREIGHT}${data}`
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      await thunkApi.dispatch(getFreight(pagination));
      return response?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.error,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// freight slice
const addFreightSlice = createSlice({
  name: 'addFreight',
  initialState,
  reducers: {},
  extraReducers: builder => {
    //get
    builder.addCase(getFreight.pending, state => {
      state.isLoading = true;
      state.data = [];
    });
    builder.addCase(getFreight.fulfilled, (state, action: any) => {
      state.isLoading = false;
      const {
        data: { freightList, freightListCount }
      } = action.payload;
      state.data = freightList?.map((item: any) => ({
        id: item?.id,
        freightNumber: item?.freightNumber,
        freightNumberCaps: String((item?.freightNumber).toUpperCase()),
        ownership: item?.ownership,
        unladenWeight: item?.unladenWeight,
        description: item?.description || 'NA',
        descriptionCaps: capitalizeFirstLetter(item?.description) || 'NA',
        freightUnladenWeightUnit: item?.freightUnladenWeightUnit,
        ownerName: item?.ownerName || 'NA',
        ownerNameCaps: capitalizeFirstLetter(item?.ownerName) || 'NA',
        length: item?.length,
        width: item?.width,
        breadth: item?.breadth,
        dimensionUnit: item?.dimensionUnit,
        isActive: item?.isActive,
        type: 'Freight'
      }));
      state.count = freightListCount;
      state.error = null;
    });
    builder.addCase(getFreight.rejected, (state, action: any) => {
      state.isLoading = false;
      state.data = [];
      state.error = action.payload?.response?.data.error || 'Something went wrong';
    });

    //Post
    builder.addCase(addFreight.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(addFreight.fulfilled, (state, action: any) => {
      state.data = action.payload;
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(addFreight.rejected, (state, action: any) => {
      state.error = action.error.message || 'Something went wrong';
      state.isLoading = false;
    });
    //Update
    builder.addCase(updateFreight.pending, state => {
      state.isLoading = true;
      state.data = [];
    });
    builder.addCase(
      updateFreight.fulfilled,
      (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.data = action.payload;
        state.error = null;
      }
    );
    builder.addCase(updateFreight.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action.payload.response.data.error || 'Something went wrong';
    });
    //DELETE
    builder.addCase(deleteFreight.pending, state => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      deleteFreight.fulfilled,
      (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.data = state.data.filter((row: any) => row.id !== action.payload.id);
        state.error = null;
      }
    );
    builder.addCase(deleteFreight.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action.payload?.response?.data?.error || 'Something went wrong';
    });
    //PATCH
    builder.addCase(PatchFreight.pending, state => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      PatchFreight.fulfilled,
      (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.data = state.data.filter((row: any) => row.id !== action.payload.id);
        state.error = null;
      }
    );
    builder.addCase(PatchFreight.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action.payload?.response?.data?.error || 'Something went wrong';
    });
  }
});

export default addFreightSlice.reducer;
